/**
 * Publish/consume side of the header breadcrumb trail.
 *
 * Detail-page titles (idea/brief names) are data the header cannot know,
 * so detail pages publish their full display trail here and the header
 * (BreadcrumbTrail) is a dumb renderer. Nothing published → row hidden.
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { TrailCrumb, useIncomingTrail } from "./trail";

interface TrailDisplayValue {
  trail: TrailCrumb[] | null;
  setTrail: (trail: TrailCrumb[] | null) => void;
}

const TrailDisplayContext = createContext<TrailDisplayValue | null>(null);

export const TrailDisplayProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [trail, setTrail] = useState<TrailCrumb[] | null>(null);
  const value = useMemo(() => ({ trail, setTrail }), [trail]);
  return (
    <TrailDisplayContext.Provider value={value}>
      {children}
    </TrailDisplayContext.Provider>
  );
};

/** Header side. Null when no page publishes (or outside the provider). */
export function usePublishedTrail(): TrailCrumb[] | null {
  return useContext(TrailDisplayContext)?.trail ?? null;
}

/**
 * Publish an explicit crumb chain while mounted. For pages that build their
 * own trail (e.g. a grid<->feed toggle where the crumbs are derived from the
 * URL) rather than the incoming-state + single-title pattern of
 * `useDetailTrail`. Pass a memoised array; pass null to publish nothing.
 */
export function usePublishTrail(crumbs: TrailCrumb[] | null): void {
  const setTrail = useContext(TrailDisplayContext)?.setTrail;
  // Key on crumb CONTENT, not array identity. Callers often build the array
  // inline (or memoise it against an unstable dep like a fresh `t` function),
  // so depending on identity would re-publish every render — and since setTrail
  // updates context, that re-renders the caller and loops forever.
  const key = crumbs
    ? crumbs.map((c) => `${c.to} ${c.label}`).join("")
    : null;
  useEffect(() => {
    if (!setTrail) return undefined;
    setTrail(crumbs);
    return () => setTrail(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- key is the content hash of crumbs
  }, [setTrail, key]);
}

/**
 * Detail-page side. Publishes the display trail (incoming chain, or the
 * canonical-list fallback on deep link/refresh, plus this page's title) to
 * the header while mounted. Returns:
 * - ``goBack``: navigate to the previous crumb (for close/delete handlers)
 * - ``navOptions``: state for onward drill-down links from this page
 */
export function useDetailTrail(opts: { fallback: TrailCrumb; title: string }): {
  goBack: () => void;
  navOptions: { state: { trail: TrailCrumb[] } };
} {
  const location = useLocation();
  const navigate = useNavigate();
  const incoming = useIncomingTrail();
  const setTrail = useContext(TrailDisplayContext)?.setTrail;

  // Depend on the fallback's primitives — callers pass the object inline,
  // so its identity changes every render.
  const fallbackTo = opts.fallback.to;
  const fallbackLabel = opts.fallback.label;
  const base = useMemo<TrailCrumb[]>(
    () =>
      incoming.length > 0
        ? incoming
        : [{ to: fallbackTo, label: fallbackLabel }],
    [incoming, fallbackTo, fallbackLabel]
  );

  const here = location.pathname + location.search;
  const crumbs = useMemo<TrailCrumb[]>(
    () => [...base, { to: here, label: opts.title }],
    [base, here, opts.title]
  );

  useEffect(() => {
    if (!setTrail) return undefined;
    setTrail(crumbs);
    return () => setTrail(null);
  }, [crumbs, setTrail]);

  const goBack = useCallback(() => {
    const prev = base[base.length - 1];
    navigate(prev.to, { state: { trail: base.slice(0, -1) } });
  }, [navigate, base]);

  const navOptions = useMemo(() => ({ state: { trail: crumbs } }), [crumbs]);

  return { goBack, navOptions };
}
